import { addToast, Button, showDialogInfo } from '@octano/global-ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { downloadFromBlob } from '../../../../utils/blob';
import { downloadSections } from '../api';
import {
  PerformanceAnalysisListQuery,
  usePerformanceAnalysisList,
} from './PerformanceAnalysisListLoader';

export interface PerformanceAnalysisFiltersDownloadProps {
  downloading?: boolean;
  onDownloadChange: (start: boolean) => void;
}

export function PerformanceAnalysisFiltersDownload({
  downloading,
  onDownloadChange,
}: PerformanceAnalysisFiltersDownloadProps) {
  const { query } = usePerformanceAnalysisList();
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_PERFORMANCE_ANALYSIS,
  );

  const handleListDownload = useCallback(
    async (query: PerformanceAnalysisListQuery) => {
      if (downloading) {
        return;
      }
      onDownloadChange(true);
      const { data, error, status } = await downloadSections(query);
      onDownloadChange(false);

      if (status === 204) {
        showDialogInfo({
          icon: {
            name: 'information',
            color: 'primary',
          },
          title: t(`listDownload.empty.title`),
          subtitle: t(`listDownload.empty.description`),
          btnConfirm: {
            text: t(`listDownload.empty.confirm`),
            color: 'primary',
            onConfirm: () => {},
          },
        });
      } else if (data && !error) {
        addToast({
          icon: 'download',
          color: 'success',
          text: t(`listDownload.success`),
        });
        const blob = new Blob([data], { type: 'vnd.ms-excel' });
        downloadFromBlob(blob, `${t(`listDownload.filename`)}.xlsx`);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`listDownload.error`),
        });
      }
    },
    [onDownloadChange, downloading, t],
  );

  return (
    <Button
      type="button"
      color="primary"
      onClick={() => {
        handleListDownload(query);
      }}
      icon="download"
      loading={downloading}
      size="md"
      text={t('listDownload.download')}
    />
  );
}
